<template>
  <RemotePaginatedList
    :filter-options="filterOptions"
    :headers="headers"
    multi-sort
    v-model="selectedItemIds"
    data-url="admin/user/"
    data-key="users"
    :refresh-trigger.sync="innerRefreshTrigger"
    v-bind="$attrs"
    class="user-list"
  >
    <template #item.is_external="{ value }">
      <v-icon v-if="value">mdi-check</v-icon>
    </template>
    <template #item.disabled="{ value, item }">
      <div
        :class="['user-list-status-div', { disabled: value }]"
        @click="enableUser(item)"
      >
        {{ value ? "Inactive" : "Active" }}
        <div class="user-list-status-bubble"></div>
      </div>
    </template>
    <template #item.permissions="{ item, value }">
      <div class="user-list-permission-wrapper">
        <v-chip
          v-for="(v, i) in item.type === 'USER' ? value : []"
          :key="i"
          small
          label
          class="user-list-contract-id"
        >
          {{ $t(`permissions.${v}`) }}
        </v-chip>

        <v-chip
          v-if="item.type !== 'USER'"
          small
          label
          class="user-list-contract-id"
        >
          {{ $t(`user_type.${item.type}`) }}
        </v-chip>
      </div>
    </template>
    <template #item.involved_contract_ids="{ value }">
      <div class="user-list-contract-id-wrapper">
        <v-chip
          v-for="(v, i) in value"
          :key="i"
          small
          label
          class="user-list-contract-id"
        >
          {{ v }}
        </v-chip>
      </div>
    </template>
    <template #item.last_login="{ value }">
      {{ value ? $parseDate(value) : "" }}
    </template>
    <template v-slot:item.actions="{ item }">
      <v-btn text color="primary" @click="editItem(item)"> Edit </v-btn>
    </template>
  </RemotePaginatedList>
</template>

<script>
import { onMounted, ref, watch } from "@vue/composition-api";
import RemotePaginatedList from "../RemotePaginatedList";
import _ from "lodash";
import { ACTION_TYPES } from "@/store/types";
import { USER_TYPE } from "@/constants";
import { DateTime } from "luxon";

export default {
  name: "UserList",
  components: { RemotePaginatedList },
  props: {
    value: Array,
    refreshTrigger: Boolean,
  },
  setup(props, { root, emit }) {
    const refresh = function () {
      innerRefreshTrigger.value = true;
    };
    onMounted(() => {
      refresh(false);
    });

    watch(
      () => props.refreshTrigger,
      (newValue) => {
        if (newValue) {
          refresh(true);
          emit("update:refresh-trigger", false);
        }
      }
    );

    // Filter
    const filterOptions = ref([
      {
        text: root.$t("user.is_external"),
        value: "is_external",
        options: [
          { text: "YES", value: true },
          { text: "NO", value: false },
        ],
      },
      {
        text: root.$t("user.username_or_email"),
        value: "username",
        contains: true,
      },
      { text: root.$t("user.name"), value: "name", contains: true },
      {
        text: root.$t("user.disabled"),
        value: "disabled",
        options: [
          { text: "Active", value: false },
          { text: "Inactive", value: true },
        ],
      },
      { text: root.$t("user.company"), value: "company", contains: true },
      { text: root.$t("user.post"), value: "post", contains: true },
      {
        text: root.$t("user.type"),
        value: "type",
        options: [
          {
            text: root.$t("user_type.SUPER_ADMIN"),
            value: USER_TYPE.SUPER_ADMIN,
          },
          { text: root.$t("user_type.ADMIN"), value: USER_TYPE.ADMIN },
          { text: root.$t("user_type.USER"), value: USER_TYPE.USER },
        ],
      },
      {
        text: root.$t("user.permissions"),
        value: "permissions.key",
        options: [
          { text: root.$t("permissions.esor.manage_sor"), value: "manage_sor" },
          { text: root.$t("permissions.esor.input_sor"), value: "input_sor" },
          {
            text: root.$t("permissions.esor.create_contract"),
            value: "create_contract",
          },
          { text: root.$t("permissions.esor.manage_nsr"), value: "manage_nsr" },
        ],
      },
      {
        text: root.$t("user.never_login"),
        value: "last_login",
        options: [
          { text: "Yes", value: false },
          { text: "No", value: true },
        ],
        single: true,
      },
      {
        text: root.$t("user.is_idle"),
        value: "last_login__lt",
        options: [
          {
            text: "YES",
            value: DateTime.fromJSDate(new Date())
              .minus({ days: 180 })
              .toISODate(),
          },
        ],
        single: true,
        exact: true,
      },
    ]);

    // Data Table
    const headers = ref([
      { text: root.$t("user.is_external"), value: "is_external", width: 100 },
      {
        text: root.$t("user.username_or_email"),
        value: "username",
        width: 200,
      },
      { text: root.$t("user.name"), value: "name", width: 200 },
      { text: root.$t("user.disabled"), value: "disabled", width: 100 },
      { text: root.$t("user.company"), value: "company", width: 300 },
      { text: root.$t("user.post"), value: "post", width: 100 },
      {
        text: root.$t("user.permissions"),
        value: "permissions",
        sortable: false,
      },
      {
        text: root.$t("user.contracts"),
        value: "involved_contract_ids",
        sortable: false,
      },
      { text: root.$t("user.last_login"), value: "last_login", width: 200 },
      { text: "", value: "actions", sortable: false, width: "50px" },
    ]);
    const innerRefreshTrigger = ref(false);

    const selectedItemIds = ref([]);
    watch(
      () => selectedItemIds.value,
      (newValue) => {
        emit("input", newValue);
      }
    );
    watch(
      () => props.value,
      (newValue, oldValue) => {
        if (_.xor(newValue, oldValue).length) {
          selectedItemIds.value = newValue;
        }
      },
      { deep: true, immediate: true }
    );

    const editItem = async function (item) {
      root.$router.push({ name: "user-detail", params: { userId: item.id } });
    };

    const deleteItem = async function (item) {
      if (await root.$dialog.confirm({ text: "Delete Section?" })) {
        const response = await root.$store.dispatch(ACTION_TYPES.CALL_API, {
          url: "admin/section/delete/",
          params: { section_id: item.id },
        });

        if (response.body.success) {
          await refresh();
        }
      }
    };

    return {
      filterOptions,

      headers,
      innerRefreshTrigger,
      selectedItemIds,

      editItem,
      deleteItem,
    };
  },
};
</script>

<style lang="less">
.user-list {
  .user-list-status-div {
    display: flex;
    align-items: center;
    color: #5bc23f;

    .user-list-status-bubble {
      margin-left: 8px;
      height: 12px;
      width: 12px;
      background-color: #5bc23f;
      border-radius: 8px;
    }

    &.disabled {
      color: #dddbdb;

      .user-list-status-bubble {
        background-color: #dddbdb;
      }
    }
  }

  .user-list-contract-id-wrapper,
  .user-list-permission-wrapper {
    width: 250px;
    overflow-x: auto;
    display: flex;

    &::-webkit-scrollbar {
      display: none;
    }

    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    .user-list-contract-id {
      flex: 0 0 auto;

      margin-right: 8px;
      background-color: #ee777a22;
      border: solid 1px #d2cbcb;
    }
  }
}
</style>
